// Plaintext-in, base64-out. Operator pastes raw secret material into
// the textarea; the value held in form state is the base64 encoding,
// ready to send as TenantSecretCreate.plaintext.
//
// UTF-8 safety: btoa() only accepts Latin-1, so we encode via the
// classic unescape(encodeURIComponent(...)) shim — handles emoji,
// accented characters, etc. without bringing in a third-party lib.

import { useEffect, useId, useState } from "react";

import { Field, errorInputStyle, inputStyle } from "./Field";

interface Base64FieldProps {
  label: string;
  value: string; // base64
  onChange: (base64Value: string) => void;
  required?: boolean;
  helper?: React.ReactNode;
  error?: string | null;
  placeholder?: string;
  rows?: number;
}

function toBase64Utf8(plain: string): string {
  // UTF-8 → bytes → base64. Throws on lone surrogates etc.; let the
  // caller surface that as a validation error.
  return btoa(unescape(encodeURIComponent(plain)));
}

function fromBase64Utf8(b64: string): string {
  try {
    return decodeURIComponent(escape(atob(b64)));
  } catch {
    return "";
  }
}

export function Base64Field({
  label,
  value,
  onChange,
  required,
  helper,
  error,
  placeholder,
  rows = 4,
}: Base64FieldProps) {
  const id = useId();
  // Local mirror of the human-readable text so the textarea stays
  // editable without round-tripping base64 → plaintext on every
  // keystroke (which would mangle non-printable input).
  const [text, setText] = useState(() => fromBase64Utf8(value));

  // Sync local text → external value when the parent resets the form
  // (e.g. successful submit clears `value` to ""). Without this the
  // textarea stays populated even though form state was wiped.
  // We only react to "value cleared" or "value differs from our cached
  // encoding" — typing inside the field updates both at once via
  // onChange, so the effect is a no-op for the common path.
  useEffect(() => {
    if (value === "") {
      setText("");
      return;
    }
    let currentEncoded = "";
    try {
      currentEncoded = text === "" ? "" : toBase64Utf8(text);
    } catch {
      currentEncoded = "";
    }
    if (currentEncoded !== value) {
      setText(fromBase64Utf8(value));
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: react to external value resets only

  return (
    <Field
      label={label}
      htmlFor={id}
      required={required}
      helper={
        helper ?? "Encoded client-side. Never displayed or stored by this UI."
      }
      error={error}
    >
      <textarea
        id={id}
        value={text}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          try {
            onChange(next === "" ? "" : toBase64Utf8(next));
          } catch {
            onChange("");
          }
        }}
        required={required}
        placeholder={placeholder}
        rows={rows}
        style={{
          ...(error ? errorInputStyle : inputStyle),
          fontFamily:
            'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
          fontSize: "0.85rem",
          resize: "vertical",
        }}
      />
    </Field>
  );
}
